import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import type { K1FundBreakdownEntry } from "@/features/K1Dashboard/services/api";

interface BreakdownBarChartProps {
  title: string;
  data: K1FundBreakdownEntry[];
  fillRate: number;
  color?: string;
  unitPlural?: string;
  isLoading?: boolean;
  isError?: boolean;
}

export default function BreakdownBarChart({
  title,
  data,
  fillRate,
  color = "#0f62fe",
  unitPlural = "funds",
  isLoading = false,
  isError = false,
}: BreakdownBarChartProps) {
  if (isLoading || isError || data.length === 0) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
        <p className="text-sm font-semibold text-gray-700 mb-3">{title}</p>
        <p className="text-xs text-gray-400 text-center py-8">
          {isError
            ? "Couldn't load this data — please retry."
            : isLoading
              ? "Loading…"
              : "No data"}
        </p>
      </div>
    );
  }

  // Chronological order (ascending year) — matters more here than for the
  // pie charts since this dimension represents a timeline.
  const sorted = [...data].sort((a, b) => Number(a.label) - Number(b.label));

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
      <p className="text-sm font-semibold text-gray-700 mb-3">{title}</p>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={sorted} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: "#6b7280" }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 11, fill: "#6b7280" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(8px)",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              fontSize: 12,
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(_value: unknown, _name: unknown, entry: any) => [
              `${entry.payload.count} ${unitPlural} (${entry.payload.percentage}%)`,
              title,
            ]}
          />
          <Bar dataKey="count" fill={color} radius={[4, 4, 0, 0]} maxBarSize={40} />
        </BarChart>
      </ResponsiveContainer>
      <p className="mt-1 text-center text-[11px] text-gray-400">
        Fill rate: {fillRate}%
      </p>
    </div>
  );
}
